import { useApp } from 'context/app-context'
import classNames from 'helpers/class-names'
import useWindowSize from 'hooks/use-window-size'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, SVGProps, useEffect, useState } from 'react'
import { Tooltip } from 'ui'

interface Props {
  children: string
  href: string
  otherHrefs?: string[]
  className?: string
  icon?: FC<SVGProps<SVGSVGElement>>
}

const SectionLink: FC<Props> = ({
  children,
  icon: IconComponent,
  href,
  otherHrefs = [],
  className = '',
}) => {
  const router = useRouter()
  const { navigationCollapsed, mobileNavigationClosed } = useApp()
  const [isActive, setIsActive] = useState<boolean>(false)
  const [isTabbable, setIsTabbable] = useState<boolean>(false)
  const { width } = useWindowSize()

  useEffect(() => {
    setIsTabbable(!mobileNavigationClosed || width > 768)
  }, [width, mobileNavigationClosed])

  useEffect(() => {
    const basePath = router.asPath.split('/')[1]
    if (
      (router.asPath.startsWith(href) && href !== '/') ||
      (router.asPath === '/' && href === '/') ||
      otherHrefs.includes(basePath)
    ) {
      setIsActive(true)
    } else {
      setIsActive(false)
    }
  }, [router])

  return (
    <Tooltip tip={navigationCollapsed ? children : null} placement="right">
      <div
        className={classNames(
          'flex items-center py-1 transition-colors duration-300 ease-in-out  hover:text-gray-50 dark:hover:text-gray-300',
          {
            'text-white': isActive,
            'text-gray-300 dark:text-gray-500': !isActive,
          },
        )}>
        <div
          className={classNames(
            'h-8 w-4 rounded-md bg-info transition duration-500 ease-in-out',
            {
              '-translate-x-2.5': isActive,
              '-translate-x-6': !isActive,
              'ml-px': !navigationCollapsed,
            },
          )}
        />
        <Link href={href}>
          <a
            tabIndex={isTabbable ? 0 : -1}
            className={classNames(
              'flex rounded py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-info/75',
              {
                'w-full': !navigationCollapsed,
                'w-auto pr-1.5': navigationCollapsed,
              },
            )}>
            <IconComponent className="ml-2 w-6" />
            <div
              className={classNames('ml-3 font-semibold', {
                'md:hidden': navigationCollapsed,
              })}>
              {children}
            </div>
          </a>
        </Link>
      </div>
    </Tooltip>
  )
}

export default SectionLink
